import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
    AlertController,
    IonBackButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonListHeader,
    IonSelect,
    IonSelectOption,
    IonTitle,
    IonToggle,
    IonToolbar,
} from '@ionic/angular/standalone';
import { UnitSelectorComponent } from '../../hgm-setup/src/module/param/components/unit-selector';
import { safeErrorString } from '../../hgm-setup/src/module/util/core';
import { LogFileProvider } from '../../hgm-setup/src/module/util/log-file-provider';
import { LOGGER } from '../../hgm-setup/src/module/util/logger';
import { GlobalSettingsProvider } from '../../providers/global-settings-provider';
import { UpdateProvider } from '../../providers/update-provider';

@Component({
    selector: 'app-tab-settings',
    templateUrl: 'tab-settings.page.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    host: { class: 'ion-page' },
    imports: [
        FormsModule,
        RouterModule,
        UnitSelectorComponent,
        IonHeader,
        IonToolbar,
        IonTitle,
        IonContent,
        IonButtons,
        IonBackButton,
        IonListHeader,
        IonItem,
        IonSelect,
        IonSelectOption,
        IonInput,
        IonToggle,
        IonLabel,
        IonIcon,
        IonButton,
    ],
})
export class TabSettingsPageComponent {
    readonly settings = inject(GlobalSettingsProvider);

    private readonly alertCtrlr = inject(AlertController);
    private readonly fileProvider = inject(LogFileProvider);
    private readonly updateProvider = inject(UpdateProvider);
    private readonly logger = inject(LOGGER);

    get isUnitSelectionReady() {
        return this.fileProvider.parseResult && !this.fileProvider.parseResult.err;
    }

    onUnitSelectionChange() {
        this.settings.onUnitSelectionChange();
    }

    get preferredCoarseWidth() {
        return this.settings.preferredCoarseWidth() / 1000;
    }

    set preferredCoarseWidth(v) {
        if (v >= 1) {
            this.settings.preferredCoarseWidth.set(v * 1000);
        } else {
            this.settings.preferredCoarseWidth.set(1000);
        }
    }

    get preferredFineWidth() {
        return this.settings.preferredFineWidth() / 1000;
    }

    set preferredFineWidth(v) {
        if (v >= 1) {
            this.settings.preferredFineWidth.set(v * 1000);
        } else {
            this.settings.preferredFineWidth.set(1000);
        }
    }

    get alwaysShowCoarse() {
        return this.settings.alwaysShowCoarse();
    }

    set alwaysShowCoarse(v) {
        this.settings.alwaysShowCoarse.set(v);
    }

    get updatesSupported() {
        return this.updateProvider.isSupported;
    }

    get updateCheckError() {
        if (this.updateProvider.checkResult() && this.updateProvider.checkResult()!.err) {
            return safeErrorString(this.updateProvider.checkResult()!.err);
        }
        return '';
    }

    get lastCheckTime() {
        if (this.updateProvider.checkResult()) {
            return this.updateProvider.checkResult()!.time.toLocaleString();
        }
        return '';
    }

    get lastSuccessfulCheckTime() {
        if (this.updateProvider.lastSuccessfulCheck()) {
            return this.updateProvider.lastSuccessfulCheck()!.toLocaleString();
        }
        return '';
    }

    get updateAvailable() {
        return this.updateProvider.available()?.latestVersion.hash !== this.updateProvider.available()?.currentVersion.hash;
    }

    async applyUpdate() {
        if (!this.updateProvider.checkInProgress) {
            try {
                await this.updateProvider.checkForUpdate();
            } catch (err) {
                this.logger.warn(err);
            }
        }

        try {
            await this.updateProvider.restartToApply();
        } catch (err) {
            const alert = await this.alertCtrlr.create({
                header: 'Applying update failed',
                message: `${safeErrorString(err)}<br>Try closing and reopening the app.`,
            });
            await alert.present();
        }
    }

    get updateCheckInProgress() {
        return this.updateProvider.checkInProgress;
    }

    checkForUpdate() {
        this.updateProvider.checkForUpdate().catch(() => {}); // will be notified separately
    }
}
